/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import {
  Fragment,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type DragEvent,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronDown,
  Eye,
  EyeOff,
  Pin,
  PinOff,
  SlidersHorizontal,
} from "lucide-react";
import { cameFrom } from "../../lib/navigation";
import { Link } from "./Link";
import { cn } from "../../lib/utils";
import { DropdownPanel } from "./DropdownPanel";
import { InfiniteList, type InfiniteListProps } from "./InfiniteList";
import { Tooltip } from "./Tooltip";
import { useDismiss } from "./useDismiss";
import {
  MIN_COLUMN_WIDTH,
  clearState,
  defaultState,
  loadState,
  placeBefore,
  saveState,
  setWidth,
  toggleHidden,
  togglePinned,
  visibleOrder,
  type TableState,
} from "./tableState";
import { useVirtualRows } from "./useVirtualRows";

export type SortOrder = "asc" | "desc";
export type TableSort = { key: string; order: SortOrder };

/** A row's name, as the link that opens it. */
export function NameCell({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="block truncate font-medium text-text-primary hover:underline">
      {children}
    </Link>
  );
}

export type Column<T> = {
  key: string;
  header: string;
  cell: (row: T) => ReactNode;
  /** Names the sort the header asks for through onSort. */
  sortKey?: string;
  defaultOrder?: SortOrder;
  /** Starting width in pixels; a column without one shares what the others leave. */
  width?: number;
  minWidth?: number;
  /** Starts pinned to the left, or hidden, until the viewer says otherwise. */
  pinned?: boolean;
  hidden?: boolean;
};

const ROW_HEIGHT = 56;
const RESIZE_STEP = 16;
const MENU_COLUMN_WIDTH = 44;
const ORDER_WORDS: Record<SortOrder, string> = { asc: "ascending", desc: "descending" };

/** One icon-only action in the Columns menu: labelled for readers, hinted on hover. */
function MenuAction({
  label,
  hint,
  icon,
  onClick,
  pressed,
  className,
}: {
  label: string;
  hint: string;
  icon: ReactNode;
  onClick: () => void;
  pressed: boolean;
  className?: string;
}) {
  return (
    <Tooltip content={hint}>
      <button
        type="button"
        aria-label={label}
        aria-pressed={pressed}
        onClick={onClick}
        className={cn(
          "grid size-7 shrink-0 cursor-pointer place-items-center rounded-md text-text-tertiary transition-colors hover:bg-bg-hover hover:text-text-primary",
          className,
        )}
      >
        {icon}
      </button>
    </Tooltip>
  );
}

/**
 * The viewer's arrangement of the table, kept in this browser under the storage key: preview
 * shows a change, update also remembers it.
 */
function useTableState<T>(storageKey: string | undefined, columns: Column<T>[]) {
  const [state, setState] = useState<TableState>(() =>
    storageKey ? loadState(storageKey, columns) : defaultState(columns),
  );
  const preview = setState;
  const update = (next: TableState) => {
    setState(next);
    if (storageKey) {
      saveState(storageKey, next);
    }
  };
  const reset = () => {
    setState(defaultState(columns));
    if (storageKey) {
      clearState(storageKey);
    }
  };
  return { state, preview, update, reset };
}

/**
 * The console's list as a table. One row per record over a fixed column layout; a sticky header
 * whose sortable columns ask the server through onSort; the same page-by-page loading the card
 * grids use, with only the rows near the viewport in the DOM; a row
 * that opens its record on click or Enter while links inside it still work on their own. Columns
 * can be resized by their edge, reordered by dragging their header, pinned to the left, and hidden
 * from the Columns menu; a storage key remembers all of that per page in this browser.
 */
export function DataTable<T>({
  caption,
  columns,
  rows,
  rowKey,
  rowHref,
  sort = null,
  onSort,
  storageKey,
  expansion,
  isLoading,
  hasMore,
  onLoadMore,
  isEmpty,
  emptyState,
  currentPage = 1,
  error = null,
  onRetry,
}: Omit<InfiniteListProps, "children"> & {
  caption: string;
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string | number;
  rowHref?: (row: T) => string;
  sort?: TableSort | null;
  onSort?: (next: TableSort) => void;
  storageKey?: string;
  /** What a row opens to below itself on click, Enter or Space; such a list is short, so never windowed. */
  expansion?: (row: T) => ReactNode;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, preview, update, reset } = useTableState(storageKey, columns);
  const byKey = useMemo(() => new Map(columns.map((column) => [column.key, column])), [columns]);
  const shown = visibleOrder(state)
    .map((key) => byKey.get(key))
    .filter((column): column is Column<T> => column !== undefined);
  const pinnedCount = shown.filter((column) => state.pinned.includes(column.key)).length;

  const bodyRef = useRef<HTMLTableSectionElement>(null);
  const rowWindow = useVirtualRows({
    count: rows.length,
    rowHeight: expansion ? 0 : ROW_HEIGHT,
    anchor: bodyRef,
  });
  const [openRows, setOpenRows] = useState<ReadonlySet<string | number>>(new Set());
  const headerRefs = useRef(new Map<string, HTMLTableCellElement>());
  const [dropBefore, setDropBefore] = useState<string | null | undefined>(undefined);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useDismiss(menuOpen, () => setMenuOpen(false), [menuRef, menuButtonRef]);

  const toggle = (key: string | number) =>
    setOpenRows((current) => {
      const next = new Set(current);
      if (!next.delete(key)) {
        next.add(key);
      }
      return next;
    });
  const open = (row: T) => {
    if (expansion) {
      toggle(rowKey(row));
      return;
    }
    if (!rowHref) {
      return;
    }
    void navigate(rowHref(row), {
      state: cameFrom(location),
    });
  };
  const onRowClick = (event: MouseEvent<HTMLTableRowElement>, row: T) => {
    if ((event.target as HTMLElement).closest("a, button, input, select, textarea")) {
      return;
    }
    open(row);
  };
  const onRowKey = (event: KeyboardEvent<HTMLTableRowElement>, row: T) => {
    const activates = event.key === "Enter" || (expansion !== undefined && event.key === " ");
    if (activates && event.target === event.currentTarget) {
      event.preventDefault();
      open(row);
    }
  };
  const opens = rowHref !== undefined || expansion !== undefined;
  const nextOrder = (column: Column<T>): SortOrder => {
    if (sort && sort.key === column.sortKey) {
      return sort.order === "asc" ? "desc" : "asc";
    }
    return column.defaultOrder ?? "asc";
  };
  const requestSort = (column: Column<T>) => {
    if (column.sortKey) {
      onSort?.({ key: column.sortKey, order: nextOrder(column) });
    }
  };

  const widthOf = (column: Column<T>) =>
    state.widths[column.key] ??
    headerRefs.current.get(column.key)?.offsetWidth ??
    column.minWidth ??
    MIN_COLUMN_WIDTH;
  const resized = (column: Column<T>, width: number) =>
    setWidth(state, column.key, width, column.minWidth ?? MIN_COLUMN_WIDTH);
  const startResize = (event: PointerEvent<HTMLSpanElement>, column: Column<T>) => {
    event.preventDefault();
    const handle = event.currentTarget;
    const startX = event.clientX;
    const startWidth = widthOf(column);
    let width = startWidth;
    handle.setPointerCapture(event.pointerId);
    const onMove = (move: globalThis.PointerEvent) => {
      width = startWidth + move.clientX - startX;
      preview(resized(column, width));
    };
    const onUp = () => {
      update(resized(column, width));
      handle.removeEventListener("pointermove", onMove);
      handle.removeEventListener("pointerup", onUp);
    };
    handle.addEventListener("pointermove", onMove);
    handle.addEventListener("pointerup", onUp);
  };
  const resizeByKey = (event: KeyboardEvent<HTMLSpanElement>, column: Column<T>) => {
    const step =
      event.key === "ArrowRight" ? RESIZE_STEP : event.key === "ArrowLeft" ? -RESIZE_STEP : 0;
    if (step !== 0) {
      event.preventDefault();
      update(resized(column, widthOf(column) + step));
    }
  };

  const onDragStart = (event: DragEvent<HTMLTableCellElement>, column: Column<T>) => {
    event.dataTransfer.setData("text/plain", column.key);
    event.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (event: DragEvent<HTMLTableCellElement>, column: Column<T>, index: number) => {
    event.preventDefault();
    const rect = event.currentTarget.getBoundingClientRect();
    const after = event.clientX > rect.left + rect.width / 2;
    setDropBefore(after ? (shown[index + 1]?.key ?? null) : column.key);
  };
  const onDrop = (event: DragEvent<HTMLTableCellElement>) => {
    event.preventDefault();
    const key = event.dataTransfer.getData("text/plain");
    if (key && dropBefore !== undefined) {
      update(placeBefore(state, key, dropBefore));
    }
    setDropBefore(undefined);
  };

  const pinOffsets = new Map<string, number>();
  let offset = 0;
  for (const column of shown.slice(0, pinnedCount)) {
    pinOffsets.set(column.key, offset);
    offset += state.widths[column.key] ?? MIN_COLUMN_WIDTH;
  }
  const pinStyle = (column: Column<T>): CSSProperties | undefined => {
    const left = pinOffsets.get(column.key);
    return left === undefined ? undefined : { position: "sticky", left };
  };
  const pinClass = (column: Column<T>, index: number) =>
    pinOffsets.has(column.key) &&
    cn(
      "z-[1] bg-bg-elevated",
      index === pinnedCount - 1 && "shadow-[inset_-1px_0_0_var(--color-border)]",
    );

  if (isEmpty && !isLoading) {
    return <>{emptyState}</>;
  }

  const spacer = (height: number) =>
    height > 0 && (
      <tr aria-hidden="true" style={{ height }}>
        <td colSpan={shown.length + 1} className="p-0" />
      </tr>
    );

  return (
    <InfiniteList
      isLoading={isLoading}
      hasMore={hasMore}
      onLoadMore={onLoadMore}
      isEmpty={isEmpty}
      currentPage={currentPage}
      error={error}
      {...(onRetry ? { onRetry } : {})}
    >
      <div className="overflow-x-auto rounded-3xl border border-border bg-bg-elevated shadow-card">
        <table className="w-full table-fixed border-collapse text-sm">
          <caption className="sr-only">{caption}</caption>
          <colgroup>
            {shown.map((column) => (
              <col
                key={column.key}
                style={state.widths[column.key] ? { width: state.widths[column.key] } : undefined}
              />
            ))}
            <col style={{ width: MENU_COLUMN_WIDTH }} />
          </colgroup>
          <thead className="sticky top-0 z-[2] bg-bg-elevated">
            <tr className="border-b border-border">
              {shown.map((column, index) => {
                const active = sort?.key === column.sortKey;
                const Icon = !active ? ArrowUpDown : sort?.order === "asc" ? ArrowUp : ArrowDown;
                return (
                  <th
                    key={column.key}
                    ref={(node) => {
                      if (node) {
                        headerRefs.current.set(column.key, node);
                      } else {
                        headerRefs.current.delete(column.key);
                      }
                    }}
                    scope="col"
                    draggable
                    onDragStart={(event) => onDragStart(event, column)}
                    onDragOver={(event) => onDragOver(event, column, index)}
                    onDragLeave={() => setDropBefore(undefined)}
                    onDrop={onDrop}
                    aria-sort={
                      !active ? undefined : sort?.order === "asc" ? "ascending" : "descending"
                    }
                    style={pinStyle(column)}
                    className={cn(
                      "relative h-11 cursor-grab overflow-hidden whitespace-nowrap px-4 text-left text-[11px] font-medium uppercase tracking-[0.12em] text-text-tertiary first:pl-5 active:cursor-grabbing",
                      pinClass(column, index),
                      dropBefore === column.key && "shadow-[inset_2px_0_0_var(--color-accent)]",
                      dropBefore === null &&
                        index === shown.length - 1 &&
                        "shadow-[inset_-2px_0_0_var(--color-accent)]",
                    )}
                  >
                    {column.sortKey ? (
                      <button
                        type="button"
                        className={cn(
                          "group inline-flex max-w-full cursor-grab items-center gap-1 rounded uppercase transition-colors hover:text-text-primary active:cursor-grabbing",
                          active && "text-text-primary",
                        )}
                        onClick={() => requestSort(column)}
                      >
                        <span className="truncate">{column.header}</span>
                        <Tooltip content={`Sort ${ORDER_WORDS[nextOrder(column)]}`}>
                          <span
                            className={cn(
                              "-my-1.5 -mr-1.5 grid size-6 shrink-0 cursor-pointer place-items-center rounded-md transition-opacity hover:bg-bg-hover",
                              active ? "opacity-100" : "opacity-0 group-hover:opacity-60",
                            )}
                          >
                            <Icon size={13} />
                          </span>
                        </Tooltip>
                      </button>
                    ) : (
                      column.header
                    )}
                    <span
                      role="separator"
                      aria-orientation="vertical"
                      aria-label={`Resize ${column.header}`}
                      tabIndex={0}
                      onPointerDown={(event) => startResize(event, column)}
                      onKeyDown={(event) => resizeByKey(event, column)}
                      className="absolute inset-y-0 right-0 w-2 cursor-col-resize after:absolute after:inset-y-2 after:right-0 after:w-px after:bg-border after:opacity-0 after:transition-opacity hover:after:opacity-100 focus-visible:outline-none focus-visible:after:bg-accent focus-visible:after:opacity-100"
                    />
                  </th>
                );
              })}
              <th scope="col" className="h-11 pr-2 text-right">
                <Tooltip content="Columns">
                  <button
                    ref={menuButtonRef}
                    type="button"
                    aria-label="Columns"
                    aria-expanded={menuOpen}
                    onClick={() => setMenuOpen((value) => !value)}
                    className="grid size-8 cursor-pointer place-items-center rounded-lg text-text-tertiary transition-colors hover:bg-bg-hover hover:text-text-primary"
                  >
                    <SlidersHorizontal size={14} />
                  </button>
                </Tooltip>
              </th>
            </tr>
          </thead>
          <tbody ref={bodyRef}>
            {spacer(rowWindow.padTop)}
            {rows.slice(rowWindow.start, rowWindow.end).map((row) => {
              const key = rowKey(row);
              const isOpen = openRows.has(key);
              return (
                <Fragment key={key}>
                <tr
                  tabIndex={opens ? 0 : undefined}
                  aria-expanded={expansion ? isOpen : undefined}
                  onClick={(event) => onRowClick(event, row)}
                  onKeyDown={(event) => onRowKey(event, row)}
                  style={{ height: ROW_HEIGHT }}
                  className={cn(
                    "group/row border-b border-border transition-colors last:border-b-0",
                    opens &&
                      "cursor-pointer hover:bg-bg-row-hover focus-visible:bg-bg-row-hover focus-visible:outline-none",
                  )}
                >
                  {shown.map((column, index) => (
                    <td
                      key={column.key}
                      style={pinStyle(column)}
                      className={cn(
                        "overflow-hidden px-4 align-middle text-text-secondary first:pl-5",
                        pinClass(column, index),
                        pinOffsets.has(column.key) && opens && "group-hover/row:bg-bg-row-hover",
                      )}
                    >
                      {column.cell(row)}
                    </td>
                  ))}
                  <td className="pr-3 text-text-secondary">
                    {expansion && (
                      <ChevronDown size={14} aria-hidden className={cn("ml-auto transition-transform", isOpen && "rotate-180")} />
                    )}
                  </td>
                </tr>
                {expansion && isOpen && (
                  <tr className="border-b border-border last:border-b-0">
                    <td colSpan={shown.length + 1} className="px-5 pt-1 pb-4">
                      {expansion(row)}
                    </td>
                  </tr>
                )}
                </Fragment>
              );
            })}
            {spacer(rowWindow.padBottom)}
          </tbody>
        </table>
      </div>
      <DropdownPanel
        triggerRef={menuButtonRef}
        isOpen={menuOpen}
        maxHeight={420}
        className="z-40 overflow-hidden"
      >
        <div ref={menuRef} className="w-72 p-2">
          <ul className="space-y-0.5">
            {state.order.map((key) => {
              const column = byKey.get(key);
              if (!column) {
                return null;
              }
              const hidden = state.hidden.includes(key);
              const pinned = state.pinned.includes(key);
              return (
                <li
                  key={key}
                  className="flex h-9 items-center gap-1 rounded-lg px-2 text-sm text-text-primary"
                >
                  <MenuAction
                    label={`${hidden ? "Show" : "Hide"} ${column.header}`}
                    hint={hidden ? "Show column" : "Hide column"}
                    icon={hidden ? <EyeOff size={14} /> : <Eye size={14} />}
                    pressed={!hidden}
                    onClick={() => update(toggleHidden(state, key))}
                    className={cn(!hidden && "text-text-secondary")}
                  />
                  <span className={cn("flex-1 truncate", hidden && "text-text-tertiary")}>
                    {column.header}
                  </span>
                  <MenuAction
                    label={`${pinned ? "Unpin" : "Pin"} ${column.header}`}
                    hint={pinned ? "Unpin" : "Pin to the left"}
                    icon={pinned ? <PinOff size={14} /> : <Pin size={14} />}
                    pressed={pinned}
                    onClick={() => update(togglePinned(state, key, widthOf(column)))}
                    className={cn(pinned && "text-accent")}
                  />
                </li>
              );
            })}
          </ul>
          <div className="mt-2 border-t border-border pt-2">
            <button
              type="button"
              onClick={() => {
                reset();
                setMenuOpen(false);
              }}
              className="h-8 w-full cursor-pointer rounded-lg px-2 text-left text-sm text-text-secondary transition-colors hover:bg-bg-hover hover:text-text-primary"
            >
              Reset columns
            </button>
          </div>
        </div>
      </DropdownPanel>
    </InfiniteList>
  );
}
